import { find } from 'lodash-es';
import { ListingTimelineField } from 'app/models/listing-timeline';
import MlsTimeline from 'app/models/mls-timeline';
import { User } from 'app/models/user';

export interface IListable {
  street_address?: string;
  unit_number?: string;
  city: string;
  state: string;
  zip_code: string;
  mls_timelines: MlsTimeline[];
  timeline_fields: ListingTimelineField[];
}

type Constructor<T = IListable> = new (...args: any[]) => T;

export default function Listable<TBase extends Constructor>(Base: TBase) {

  return class extends Base {

    status() {
      switch (this.timelineFieldValue('status').toLowerCase()) {
        case 'active':
          return 'For Sale';
        case 'pending':
        case 'active under contract':
          return 'Pending Sale';
        case 'closed':
          let status = 'Sold';
          if (this.timelineFieldValue('settlement-date')) {
            status += ' ' + this.timelineFieldValue('settlement-date');
          }
          return status;
        default:
          return this.timelineFieldValue('status');
      }
    }

    // This native property types in our system are not as consumer friendly as we'd like
    propertyType() {
      let propertyType = this.timelineFieldValue('property-type')

      switch (propertyType) {
        case 'Detached':
          return 'Single Family Detached Home';
        case 'Twin/Semi-detached':
          return 'Twin/Semi-Detached Home';
        case 'Manufactured':
          return 'Manufactured Home';
        case 'Mobile Pre-1976':
          return 'Mobile Home';
        default:
          return propertyType;
      }
    }

    price() {
      switch (this.timelineFieldValue('status').toLowerCase()) {
        case 'active':
        case 'coming soon':
        case 'active under contract':
        case 'pending':
          return this.timelineFieldValue('current-amount-in-cents');
        case 'closed':
          return this.timelineFieldValue('settlement-amount-in-cents');
        default:
          return null;
      }
    }

    totalFinishedSqft(){
      let aboveGradeFinishedSqftTimelineField = find(this.timeline_fields, timelineField => timelineField.slug === 'above-grade-finished-sqft')
      let belowGradeFinishedSqftTimelineField = find(this.timeline_fields, timelineField => timelineField.slug === 'below-grade-finished-sqft')

      let total: any = 0;

      if(aboveGradeFinishedSqftTimelineField){
        total += aboveGradeFinishedSqftTimelineField.formatted_value;
      }

      if(belowGradeFinishedSqftTimelineField){
        total += belowGradeFinishedSqftTimelineField.formatted_value;
      }

      return total ? total : null;
    }

    timelineFieldValue(timelineFieldSlug: string) {
      let timelineField = find(this.timeline_fields, timelineField => timelineField.slug === timelineFieldSlug)
      return timelineField ? timelineField.formatted_value : null;
    }

    isPending() {
      return this.timelineFieldValue('status').toLowerCase() == 'pending'
    }

    isActiveUnderContract() {
      return this.timelineFieldValue('status').toLowerCase() == 'active_under_contract'
    }

    isClosed() {
      return this.timelineFieldValue('status').toLowerCase() == 'closed'
    }

    isComingSoon() {
      return this.timelineFieldValue('status').toLowerCase() == 'coming soon'
    }

    neighborhood() {
      return this.timelineFieldValue('neighborhood');
    }

    bathrooms() {
      let fullBathrooms = this.timelineFieldValue('full-bathrooms');
      let halfBathrooms = this.timelineFieldValue('half-bathrooms');
      let totalBathrooms = this.timelineFieldValue('total-bathrooms');

      if (!fullBathrooms && totalBathrooms) {
        return totalBathrooms;
      }

      let bathrooms = fullBathrooms;
      if (halfBathrooms && parseInt(halfBathrooms)) {
        bathrooms += `/${halfBathrooms}`;
      }

      return bathrooms;
    }

    title(options: { [includeCommas: string]: boolean } = {}) {
      options.includeCommas = (options && options['includeCommas'] !== undefined) ? options.includeCommas : true;

      let address = this.street_address

      if (this.unit_number) {
        address = address.concat(` ${this.unit_number}`);
      }

      address = address.concat(`, ${this.city}, ${this.state} ${this.zip_code}`);

      if (!options.includeCommas) {
        address = address.replace(',', '');
      }

      return address;
    }

    associationFee() : string {
      // return in order most likely
      if (this.timelineFieldValue('hoa-fees')) {
        return `${this.timelineFieldValue('hoa-fees')} ${this.feeSchedule('hoa-fee-schedule')}`.trim();
      }

      if (this.timelineFieldValue('condo-fees')) {
        return `${this.timelineFieldValue('condo-fees')} ${this.feeSchedule('condo-fee-schedule')}`.trim();
      }

      if (this.timelineFieldValue('masters-association-fees')) {
        return `${this.timelineFieldValue('masters-association-fees')} ${this.feeSchedule('masters-association-fee-schedule')}`.trim();
      }

      return null;
    }

    private feeSchedule(scheduleSlug: string) : string {
      const value = this.timelineFieldValue(scheduleSlug);

      if(!value || value === 'N/A') return '';

      return `/ ${value}`;
    }

    primaryMlsListingId() : string {
      return this.mls_timelines.find(mlsTimeline => mlsTimeline.primary)?.listing_id || null;
    }
  };
}
